<template>
  <div
    class="aj-upload"
    :class="{ 'aj-upload--dragover': isDragging }"
    @dragenter.prevent="onDragEnter"
    @dragover.prevent="onDragOver"
    @dragleave.prevent="onDragLeave"
    @drop.prevent="onDrop"
  >
    <div class="aj-upload__toolbar">
      <slot name="upload-btn" :trigger="triggerUpload" :disabled="isUploadDisabled">
        <el-button type="primary" icon="el-icon-upload" :disabled="isUploadDisabled" @click="triggerUpload">
          {{ uploadBtnText }}
        </el-button>
      </slot>
      <el-button v-if="allowClear && allFiles.length" type="text" @click="clearAllFiles">清空</el-button>
      <span v-if="showLimitInfo" class="aj-upload__limit">已上传 {{ allFiles.length }} / {{ realLimit === Infinity ? '不限' : realLimit }} 个{{ acceptTypeText ? `，支持 ${acceptTypeText}` : '' }}{{ maxFileSizeText ? `，单文件不超过 ${maxFileSizeText}` : '' }}</span>
    </div>

    <input
      ref="fileInput"
      class="aj-upload__input"
      type="file"
      :accept="acceptType"
      :multiple="multiple"
      @change="handleFileChange"
    >

    <!-- 拖拽区域（在上） -->
    <div
      v-if="alwaysShowDropzone || !allFiles.length"
      class="aj-upload__drop-zone"
      :class="{ 'is-disabled': isUploadDisabled }"
      @click="triggerUpload"
    >
      <i class="el-icon-upload"></i>
      <span v-if="allFiles.length">继续将文件拖到此处，或<em>点击选择文件</em></span>
      <span v-else>将文件拖到此处，或<em>点击选择文件</em></span>
    </div>

    <!-- 文件列表（在下） -->
    <div v-if="allFiles.length" class="aj-upload__list">
      <div v-for="file in allFiles" :key="file.id" class="aj-upload__item">
        <div class="aj-upload__meta">
          <i class="el-icon-document"></i>
          <div class="aj-upload__text">
            <div class="aj-upload__name" :title="file.name">{{ file.name }}</div>
            <div class="aj-upload__desc">{{ formatFileSize(file.size) }}<span v-if="file.extension"> · {{ String(file.extension).toUpperCase() }}</span></div>
          </div>
        </div>
        <div class="aj-upload__actions">
          <el-button type="text" @click="previewFile(file)">预览</el-button>
          <el-button type="text" @click="downloadFile(file)">下载</el-button>
          <el-button type="text" class="aj-upload__danger" @click="removeFile(file)">删除</el-button>
        </div>
      </div>
    </div>

    <el-dialog v-dialog-drag-resize :visible.sync="imagePreviewVisible" width="80%" append-to-body title="图片预览">
      <img :src="previewImageUrl" alt="preview" class="aj-upload__image-preview">
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'AjUpload',
  props: {
    multiple: { type: Boolean, default: false },
    uploadType: { type: String, default: 'button' },
    acceptType: { type: String, default: '' },
    limit: { type: Number, default: 1 },
    historyFiles: { type: Array, default: () => [] },
    uploadBtnText: { type: String, default: '选择文件' },
    showLimitInfo: { type: Boolean, default: true },
    allowClear: { type: Boolean, default: true },
    maxFileSize: { type: Number, default: 0 },
    uploadRequest: { type: Function, default: null },
    uploadUrl: { type: String, default: '' },
    uploadHeaders: { type: Object, default: () => ({}) },
    uploadData: { type: Object, default: () => ({}) },
    responseAdapter: { type: Function, default: null },
    disabled: { type: Boolean, default: false },
    // 拖拽区是否常驻（即使已有文件也显示），便于连续上传
    alwaysShowDropzone: { type: Boolean, default: false }
  },
  data () {
    return {
      allFiles: [],
      imagePreviewVisible: false,
      previewImageUrl: '',
      uploading: false,
      isDragging: false,
      dragCounter: 0
    }
  },
  computed: {
    realLimit () {
      return Number.isFinite(this.limit) ? this.limit : Infinity
    },
    isUploadDisabled () {
      return this.disabled || this.uploading || this.allFiles.length >= this.realLimit
    },
    acceptTypeText () {
      if (!this.acceptType) return ''
      const typeMap = {
        'image/*': '图片',
        'video/*': '视频',
        'audio/*': '音频',
        'application/pdf': 'PDF'
      }
      return typeMap[this.acceptType] || this.acceptType.split(',').map(type => type.trim().replace('.', '').toUpperCase()).join('、')
    },
    maxFileSizeText () {
      return this.maxFileSize ? this.formatFileSize(this.maxFileSize) : ''
    }
  },
  watch: {
    historyFiles: {
      immediate: true,
      deep: true,
      handler (newVal) {
        this.allFiles = this.uniqueFiles((newVal || []).map(file => this.normalizeExistingFile(file)))
      }
    }
  },
  methods: {
    triggerUpload () {
      if (this.isUploadDisabled) {
        this.$message.warning(this.disabled ? '当前不可上传文件' : `最多只能上传 ${this.realLimit} 个文件`)
        return
      }
      this.$refs.fileInput && this.$refs.fileInput.click()
    },
    // 拖拽事件处理（用 dragCounter 解决子元素触发 dragleave 的抖动问题）
    onDragEnter () {
      if (this.isUploadDisabled) return
      this.dragCounter++
      this.isDragging = true
    },
    onDragOver () {
      if (this.isUploadDisabled) return
      this.isDragging = true
    },
    onDragLeave () {
      this.dragCounter--
      if (this.dragCounter <= 0) {
        this.isDragging = false
        this.dragCounter = 0
      }
    },
    async onDrop (e) {
      this.isDragging = false
      this.dragCounter = 0
      if (this.isUploadDisabled) {
        this.$message.warning(this.disabled ? '当前不可上传文件' : `最多只能上传 ${this.realLimit} 个文件`)
        return
      }
      const files = Array.from(e.dataTransfer.files || [])
      if (!files.length) return
      await this.processFiles(files)
    },
    async handleFileChange (event) {
      const files = Array.from((event.target && event.target.files) || [])
      if (!files.length) return
      await this.processFiles(files)
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },
    // 点击选择和拖拽共用的文件处理逻辑
    async processFiles (files) {
      try {
        this.uploading = true
        const nextFiles = []
        for (const rawFile of files) {
          this.beforeUpload(rawFile)
          if (this.checkFileDuplicate(rawFile)) {
            this.$message.warning(`文件 ${rawFile.name} 已存在，已自动跳过`)
            continue
          }
          const normalizedFile = await this.processFile(rawFile)
          nextFiles.push(normalizedFile)
        }
        if (!nextFiles.length) return
        this.allFiles = this.uniqueFiles([...(this.allFiles || []), ...nextFiles]).slice(0, this.realLimit)
        this.emitChange()
        this.$message.success('文件处理完成')
      } catch (error) {
        this.$message.error(error.message || '文件处理失败')
        this.$emit('upload-error', error)
      } finally {
        this.uploading = false
      }
    },
    async processFile (rawFile) {
      if (this.uploadRequest) {
        const result = await this.uploadRequest(rawFile, { fileList: [...this.allFiles] })
        return this.normalizeUploadedFile(result, rawFile)
      }
      if (this.uploadUrl) {
        const formData = new FormData()
        formData.append('file', rawFile)
        Object.keys(this.uploadData || {}).forEach(key => {
          if (this.uploadData[key] !== undefined && this.uploadData[key] !== null) {
            formData.append(key, this.uploadData[key])
          }
        })
        const response = await axios.post(this.uploadUrl, formData, {
          headers: {
            ...this.uploadHeaders,
            'Content-Type': 'multipart/form-data'
          }
        })
        const payload = this.responseAdapter ? this.responseAdapter(response.data, rawFile) : response.data
        return this.normalizeUploadedFile(payload, rawFile)
      }
      return this.createLocalFile(rawFile)
    },
    normalizeUploadedFile (payload, rawFile) {
      const source = payload && payload.data ? payload.data : payload
      return this.normalizeFileObject({
        id: source && (source.id || source.fileId || source.uid),
        name: source && (source.name || source.fileName) ? (source.name || source.fileName) : rawFile.name,
        url: source && source.url ? source.url : URL.createObjectURL(rawFile),
        size: source && source.size ? source.size : rawFile.size,
        extension: source && source.extension ? source.extension : this.getFileExtension(rawFile.name),
        mime_type: source && (source.mime_type || source.contentType) ? (source.mime_type || source.contentType) : rawFile.type,
        type: source && source.type ? source.type : this.getFileCategory(rawFile),
        raw: rawFile,
        storageKey: source && (source.storageKey || source.fileKey) ? (source.storageKey || source.fileKey) : ''
      })
    },
    normalizeExistingFile (file) {
      return this.normalizeFileObject({
        ...file,
        id: file && (file.id || file.fileId || file.uid),
        name: file && (file.name || file.fileName),
        url: file && file.url,
        size: file && file.size,
        extension: file && file.extension ? file.extension : this.getFileExtension(file && (file.name || file.fileName || '')),
        mime_type: file && (file.mime_type || file.contentType || ''),
        type: file && file.type ? file.type : this.getFileCategory(file)
      })
    },
    normalizeFileObject (file) {
      return {
        id: file.id || this.generateUniqueId(),
        name: file.name || '未命名文件',
        url: file.url || '',
        size: file.size || 0,
        extension: file.extension || this.getFileExtension(file.name || ''),
        mime_type: file.mime_type || '',
        type: file.type || this.getFileCategory(file),
        raw: file.raw || null,
        storageKey: file.storageKey || ''
      }
    },
    createLocalFile (rawFile) {
      return this.normalizeFileObject({
        name: rawFile.name,
        url: URL.createObjectURL(rawFile),
        size: rawFile.size,
        extension: this.getFileExtension(rawFile.name),
        mime_type: rawFile.type,
        type: this.getFileCategory(rawFile),
        raw: rawFile
      })
    },
    emitChange () {
      const snapshot = [...this.allFiles]
      this.$emit('update:historyFiles', snapshot)
      this.$emit('upload-success', snapshot)
    },
    clearAllFiles () {
      this.$confirm('确定要清空所有已上传的文件吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.revokeLocalUrls(this.allFiles)
        this.allFiles = []
        this.emitChange()
        this.$message.success('已清空所有文件')
      }).catch(() => {})
    },
    previewFile (file) {
      if (!file.url) {
        this.$message.warning('当前文件暂无可预览地址')
        return
      }
      if (file.type === 'image') {
        this.previewImageUrl = file.url
        this.imagePreviewVisible = true
        return
      }
      window.open(file.url, '_blank')
    },
    downloadFile (file) {
      if (!file.url) {
        this.$message.warning('当前文件暂无可下载地址')
        return
      }
      const link = document.createElement('a')
      link.href = file.url
      link.download = file.name
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    removeFile (file) {
      this.$confirm('确定要删除该文件吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.revokeLocalUrls([file])
        this.allFiles = this.allFiles.filter(item => item.id !== file.id)
        this.emitChange()
        this.$message.success('文件删除成功')
      }).catch(() => {})
    },
    beforeUpload (file) {
      if (this.acceptType) {
        const acceptTypes = this.acceptType.split(',').map(type => type.trim()).filter(Boolean)
        const fileExt = this.getFileExtension(file.name)
        const fileMime = file.type || ''
        const isMatched = acceptTypes.some(type => {
          if (type.endsWith('*')) return fileMime.startsWith(type.replace('*', ''))
          if (type.startsWith('.')) return fileExt === type.slice(1).toLowerCase()
          return fileMime === type || fileExt === type.toLowerCase()
        })
        if (!isMatched) {
          throw new Error(`文件 ${file.name} 类型不符合要求`)
        }
      }
      if (this.maxFileSize && file.size > this.maxFileSize) {
        throw new Error(`文件 ${file.name} 超过大小限制`)
      }
      if (this.allFiles.length >= this.realLimit) {
        throw new Error(`最多只能上传 ${this.realLimit} 个文件`)
      }
    },
    checkFileDuplicate (file) {
      return this.allFiles.some(item => (item.url && item.url === file.url) || (item.name === file.name && Number(item.size || 0) === Number(file.size || (file.raw && file.raw.size) || 0)))
    },
    getFileExtension (name) {
      return String(name || '').split('.').pop().toLowerCase()
    },
    getFileCategory (file) {
      const mime = (file && (file.type || file.mime_type || '')) || ''
      const ext = this.getFileExtension(file && (file.name || file.fileName || ''))
      if (mime.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(ext)) return 'image'
      if (mime.startsWith('video/') || ['mp4', 'avi', 'mov', 'wmv'].includes(ext)) return 'video'
      if (mime.startsWith('audio/') || ['mp3', 'wav', 'ogg'].includes(ext)) return 'audio'
      return 'document'
    },
    uniqueFiles (fileList) {
      const map = new Map()
      ;(fileList || []).forEach(file => {
        if (!file) return
        const key = file.url || `${file.name}_${file.size}`
        if (!map.has(key)) map.set(key, file)
      })
      return Array.from(map.values())
    },
    generateUniqueId () {
      return `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
    },
    formatFileSize (bytes) {
      if (!bytes) return '未知'
      const units = ['B', 'KB', 'MB', 'GB']
      const level = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
      return `${(bytes / Math.pow(1024, level)).toFixed(level === 0 ? 0 : 2)} ${units[level]}`
    },
    revokeLocalUrls (files) {
      ;(files || []).forEach(file => {
        if (file && file.url && String(file.url).startsWith('blob:')) {
          URL.revokeObjectURL(file.url)
        }
      })
    },
    resetAllState () {
      this.revokeLocalUrls(this.allFiles)
      this.allFiles = []
      this.previewImageUrl = ''
      this.imagePreviewVisible = false
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    }
  },
  beforeDestroy () {
    this.revokeLocalUrls(this.allFiles)
  }
}
</script>

<style scoped>
.aj-upload {
  width: 100%;
}

.aj-upload__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.aj-upload__limit {
  color: #909399;
  font-size: 12px;
}

.aj-upload__input {
  display: none;
}

.aj-upload__list {
  margin-top: 16px;
  display: grid;
  gap: 12px;
}

.aj-upload__item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fafbfc;
  overflow: hidden;
}

.aj-upload__meta {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
  overflow: hidden;
}

.aj-upload__meta i {
  color: #409eff;
  font-size: 18px;
  flex-shrink: 0;
}

.aj-upload__text {
  min-width: 0;
  flex: 1;
  overflow: hidden;
}

.aj-upload__name {
  color: #303133;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.aj-upload__desc {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}

.aj-upload__actions {
  flex-shrink: 0;
  white-space: nowrap;
}

.aj-upload__danger {
  color: #f56c6c;
}

.aj-upload__empty {
  margin-top: 16px;
  padding: 20px 16px;
  border: 1px dashed #dcdfe6;
  border-radius: 8px;
  color: #909399;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

/* 拖拽区域 */
.aj-upload__drop-zone {
  margin-top: 16px;
  margin-bottom: 16px;
  padding: 28px 16px;
  border: 1px dashed #d0d5dd;
  border-radius: 8px;
  color: #909399;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: border-color .2s, background .2s;
}

/* 达到上限或禁用态：变灰且不可交互 */
.aj-upload__drop-zone.is-disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.aj-upload__drop-zone.is-disabled:hover {
  border-color: #d0d5dd;
  background: transparent;
}

.aj-upload__drop-zone i {
  font-size: 32px;
  color: #c0c4cc;
}

.aj-upload__drop-zone em {
  color: #409eff;
  font-style: normal;
}

.aj-upload__drop-zone:hover {
  border-color: #409eff;
  background: #f5faff;
}

/* 拖拽悬停高亮 */
.aj-upload--dragover .aj-upload__drop-zone,
.aj-upload--dragover {
  border-color: #409eff;
  background: #ecf5ff;
}

.aj-upload--dragover .aj-upload__drop-zone i {
  color: #409eff;
}

.aj-upload__image-preview {
  display: block;
  width: 100%;
}
</style>
